import { useCallback, useEffect, useMemo } from "react";
import {
  getCalendarId,
  getComposeMode,
  getItemId,
  getItemSubject,
  getItemType,
} from "@platform/utils/item.util";
import useUserStore from "@store/useUserStore";
import useCompanyStore from "@store/useCompanyStore";
import usePlatformStore from "@store/usePlatformStore";
import useSystemStore from "@store/useSystemStore";
import { getCurrentTabUrl } from "@platform/utils/chrome.util";
import { useItemChanged } from "@platform/hooks/useItemChanged";
import { usePlatformNavigation } from "./usePlatformNavigation";
import { GrantProvider } from "@app-types/common.type";
import { ItemType } from "@enums/common.enum";

export function usePlatformItem() {
  const { hasSession, activeUser } = useUserStore();
  const { navigateInChrome } = usePlatformNavigation();
  const { setPlatformData, setError, isChrome, isOutlook } = usePlatformStore();
  const { setCurrentUrl } = useCompanyStore();
  const { setIsAppLoading } = useSystemStore();

  const googleGrantEmail = useMemo(
    () => activeUser?.grants?.find((g) => g.provider === GrantProvider.GOOGLE)?.username ?? null,
    [activeUser]
  );

  const refetchPlatformData = useCallback(async (): Promise<boolean> => {
    try {
      let itemType = await getItemType();
      let calendarId = null;

      if (isChrome()) {
        const url = await getCurrentTabUrl();

        const shouldUpdateData = await navigateInChrome(url, itemType);
        if (!shouldUpdateData) return false;

        if (itemType === ItemType.COMPANY) setCurrentUrl(url);

        calendarId = await getCalendarId();
      }

      setPlatformData({
        itemId: await getItemId(),
        itemSubject: await getItemSubject(),
        itemType,
        isComposeMode: getComposeMode(),
        calendarId,
      });
      setError(null);

      return true;
    } catch {
      setError("Failed to get item details.");
      return false;
    }
  }, [isChrome, navigateInChrome, setCurrentUrl, setPlatformData, setError]);

  useEffect(() => {
    void refetchPlatformData();
  }, [hasSession, activeUser, googleGrantEmail]);

  // Listen for item selection changes (works on both platforms):
  // - Chrome: URL changes and Gmail split view selection changes
  // - Outlook: Office.EventType.ItemChanged when the task pane is pinned
  useItemChanged(() => {
    // Outlook pinned mode has no navigateInChrome routing step, so mirror the transition
    // loader Chrome shows there: hide the previous item behind <AppLoader/> while the newly
    // selected item re-syncs. Chrome keeps its own (conditional) trigger in navigateInChrome.
    if (isOutlook()) setIsAppLoading(true);
    void refetchPlatformData();
  });

  return { refetchPlatformData };
}
